// CRUD de alunos e autenticação (login por DRE/email + senha, identificação por DRE/nome/email)
import { collection, doc, addDoc, updateDoc, deleteDoc, getDocs, query, where } from "firebase/firestore";
import type { Student } from '../types';
import { db, periodBase } from './config';
import { hashPassword, verifyPassword, needsRehash } from '../utils/crypto';

export async function addStudent(pid: string, s: Omit<Student,"id">) {
  await addDoc(collection(db, periodBase(pid)+"/students"), s);
}

export async function updateStudent(pid: string, id: string, data: Partial<Student>) {
  await updateDoc(doc(db, periodBase(pid)+"/students/"+id), data);
}

export async function deleteStudent(pid: string, id: string) {
  await deleteDoc(doc(db, periodBase(pid)+"/students/"+id));
}

// LGPD Art. 18 VI — direito ao esquecimento.
// Apaga o cadastro do aluno + TODOS os registros de presença dele no periodo.
export async function deleteStudentAndAttendance(pid: string, studentId: string) {
  // 1) apaga registros de presenca
  const attRef = collection(db, periodBase(pid)+"/attendance");
  const snap = await getDocs(query(attRef, where("studentId","==",studentId)));
  await Promise.all(snap.docs.map(d => deleteDoc(doc(db, periodBase(pid)+"/attendance/"+d.id))));
  // 2) apaga o aluno
  await deleteDoc(doc(db, periodBase(pid)+"/students/"+studentId));
}

export async function findByCredentials(pid: string, dreOrEmail: string, pass: string): Promise<Student|null> {
  if (!navigator.onLine) {
    try {
      const cached = localStorage.getItem('gf_students_'+pid);
      if (cached) {
        const arr = JSON.parse(cached) as Student[];
        const s = arr.find(s => s.dre===dreOrEmail.trim() || s.email?.toLowerCase()===dreOrEmail.trim().toLowerCase());
        if (s && s.password) return await verifyPassword(pass, s.password) ? s : null;
      }
    } catch(e) { console.warn('[GeoFreq] Cache offline inválido:', e); }
    return null;
  }
  const ref = collection(db, periodBase(pid)+"/students");
  const snap1 = await getDocs(query(ref, where("dre","==",dreOrEmail.trim())));
  if (!snap1.empty) {
    const s={id:snap1.docs[0].id,...snap1.docs[0].data()} as Student;
    if (s.password && await verifyPassword(pass, s.password)) {
      // auto-migrate plaintext/sha256 → pbkdf2
      if (needsRehash(s.password)) { const h=await hashPassword(pass); updateDoc(doc(db,periodBase(pid)+'/students/'+s.id),{password:h}).catch(()=>{}); }
      try{ const {password:_pw,...safeS}=s; const arr=JSON.parse(localStorage.getItem('gf_students_'+pid)||'[]') as Student[]; if(!arr.find(x=>x.id===safeS.id)){ arr.push(safeS); localStorage.setItem('gf_students_'+pid,JSON.stringify(arr)); } }catch(e){}
      return s;
    }
    return null;
  }
  const snap2 = await getDocs(query(ref, where("email","==",dreOrEmail.trim().toLowerCase())));
  if (!snap2.empty) {
    const s={id:snap2.docs[0].id,...snap2.docs[0].data()} as Student;
    if (s.password && await verifyPassword(pass, s.password)) {
      if (needsRehash(s.password)) { const h=await hashPassword(pass); updateDoc(doc(db,periodBase(pid)+'/students/'+s.id),{password:h}).catch(()=>{}); }
      try{ const {password:_pw,...safeS}=s; const arr=JSON.parse(localStorage.getItem('gf_students_'+pid)||'[]') as Student[]; if(!arr.find(x=>x.id===safeS.id)){ arr.push(safeS); localStorage.setItem('gf_students_'+pid,JSON.stringify(arr)); } }catch(e){}
      return s;
    }
    return null;
  }
  return null;
}

export async function findByIdentity(pid: string, name:string, dre:string, email:string): Promise<Student|null> {
  if (!navigator.onLine) {
    try {
      const cached = localStorage.getItem('gf_students_'+pid);
      if (cached) {
        const arr = JSON.parse(cached) as Student[];
        const s = arr.find(s => s.dre===dre.trim());
        const _offNameOk = s && (s.name.trim().toLowerCase()===name.trim().toLowerCase() || s.name.trim().localeCompare(name.trim(),'pt-BR',{sensitivity:'base'})===0);
        if (_offNameOk && s.email.trim().toLowerCase()===email.trim().toLowerCase()) return s;
        return null;
      }
    } catch(e) { console.warn('[GeoFreq] Cache offline inválido:', e); }
    return null;
  }
  const snap = await getDocs(query(collection(db, periodBase(pid)+"/students"), where("dre","==",dre.trim())));
  if (snap.empty) return null;
  const s={id:snap.docs[0].id,...snap.docs[0].data()} as Student;
  const _nameOk = s.name.trim().toLowerCase()===name.trim().toLowerCase() || s.name.trim().localeCompare(name.trim(),'pt-BR',{sensitivity:'base'})===0;
  if (_nameOk && s.email.trim().toLowerCase()===email.trim().toLowerCase()) return s;
  return null;
}
